import {
  Address,
  AggregatedStatus,
  GatewayType,
  TargetRealmType,
  ViError,
  _Action,
  _Link,
} from '../../models';
import { EvDevice } from '../general-management/general-management.model';
import { InvitationAccessLevel } from '../invitations/invitations.model';
import { User, UserV3, CompanyUser } from '../users/users.model';
import { InstallationType } from '@store';

export interface DataWithCursorResponse<T> {
  data: T;
  cursor: { next: string };
}

export enum VoucherType {
  DeltaTemperatureRoomModulations = '01',
  DeltaTemperatureRoomOnOff = '02',
}

export interface SrcLiteVoucher {
  voucherCode: string;
}

export interface SrcLiteVoucherStatus {
  type: VoucherType;
  status: VoucherStatus;
}

export enum VoucherStatus {
  FreeToUse = 'FreeToUse',
  NotUsable = 'NotUsable',
}

export interface MaterialDetailsResponse {
  materialDetails: MaterialDetails[];
}

export interface MaterialDetails {
  name: string;
  material: string;
  description: string;
  productGroup: string;
}

export interface DeviceImageResponse {
  images: DeviceImage[];
}

export interface DeviceImage {
  material: string;
  format: string;
  url: string;
}

export interface MaterialName {
  material: string;
  name: string;
  supportsServiceDocuments: boolean;
}

export interface GatewayMaterialDetailsResponse {
  materialDetails: MaterialGatewayName[];
}

export interface MaterialGatewayName {
  name: string;
  material: string;
  description: string;
}

export interface AccessList {
  buildingManager: CommercialAccessAccount;
  familyMembers: User[];
  maintainer: {
    customerNumber: string;
    accounts: User[];
  };
  maintainers?: MaintainerAccessAccount[];
  operator: CommercialAccessAccount;
  owner: User;
  resourceId: number;
  serviceContractor: CommercialAccessAccount;
  supporters: User[];
  commercialOwner: CustomerNumberWithAccounts;
  partners: CustomerNumberWithAccounts[];
  consumers: UserV3[];
}

export interface AccessListUserData {
  firstName: string;
  lastName: string;
  familyName?: string;
  email: string;
  role: string;
  viAccount: string;
  id?: number;
}

export interface AccessListCommercialData {
  companyName?: string; // not accessible - waiting for backend's implementation
  companyOwnerNumber: string;
  companyAccounts: AccessListUserData[];
  partners: CustomerNumberWithAccounts[];
}

export interface AccessListUserByCustomerNumber {
  buildingManager?: AccessListUserByCustomerNumberData;
  serviceContractor?: AccessListUserByCustomerNumberData;
  operator?: AccessListUserByCustomerNumberData;
}

export interface CommercialAccessAccount {
  customerNumber: string;
  companyName: string;
  accounts: Account[];
}

export interface MaintainerAccessAccount {
  customerNumber: number;
  companyName: string;
  employees: CompanyUser[];
  loaded: boolean;
  loading: boolean;
  error: ViError | unknown;
}

export interface Account {
  id: number;
  viAccount: string;
  salutation: string;
  salutationV2?: any;
  firstName: string;
  lastName: string;
  email: string;
  emailVerified: boolean;
  locale: string;
  address?: any;
  mobileNumber?: any;
  phoneNumber?: any;
  faxNumber?: any;
  role: string;
  group: string;
  isCEO: boolean;
  isDeleted: boolean;
  createdAt: Date;
  editedAt: Date;
  privacyPolicyAcceptedAt?: any;
  privacyPolicyRequester?: any;
}
export interface ActiveErrorResponse {
  content?: {
    appearedAt: string;
    deviceId: string;
    equipmentType: string;
    errorCode: string;
    errorDescription: string;
    errorEventType: string;
    gatewaySerial: string;
    modelId: string;
    description?: string;
    longDescription?: string;
    title?: string;
  };
  createdAt: string;
  editedAt: string;
  editorGroup: string[];
  id: number;
  isAutomated: boolean;
  status: ActiveErrorStatus;
  targetGroup: string[];
  type: string;
}

export enum ActiveErrorStatus {
  Unknown = 'Unknown',
  New = 'New',
  Seen = 'Seen',
  Accepted = 'Accepted',
  Dismissed = 'Dismissed',
  Done = 'Done',
}

export interface DepartmentResponse {
  cursor?: { next: string };
  data: {
    createdAt: string;
    editedAt: string;
    email: string;
    id: number;
    locale: string;
    name: string;
  }[];
}

export interface ResetGatewayResponse {
  data: {
    success: boolean;
    message: string;
    reason: string;
  };
}

export interface MaintenancePackagesResponse {
  link: string;
}

export interface GatewayData {
  serial: string;
  password: string;
}

export interface InstallationOwnerData {
  salutation: string;
  firstName: string;
  lastName: string;
  privacyPolicyAccepted: boolean;
  privacyPolicyAcceptanceUtc: string;
}

export interface TransferInstallationAddress {
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  country: string;
  region?: string;
}

export interface CompanyGatewaysResponse {
  cursor?: {
    next: string;
  };
  data: {
    aggregatedStatus: AggregatedStatus;
    createdAt: string;
    devices: EvDevice[];
    firmwareUpdateFailureCounter: number;
    gatewayType: GatewayType;
    installationId: number;
    lastStatusChangedAt: string;
    producedAt: string;
    registeredAt: string;
    serial: string;
    targetRealm: TargetRealmType;
    version: string;
  }[];
}

export interface EquipmentDocumentResponse {
  printNo: string;
  name: string;
  description: string;
  printDate: string;
  documentTypeId: string;
  documentTypeName: string;
  url: string;
  size: string;
}

export interface InstallationSummary {
  equipmentNumber: string;
  materialImage: string;
  materialName: string;
  name: string;
}

export interface InstallationEquipment {
  address: Address;
  aggregatedStatus: AggregatedStatus;
  description: string;
  endUserWlanCommissioned: boolean;
  heatingType: HeatingType;
  id: number;
  installationType: InstallationType;
  ownedByMaintainer: boolean;
  registeredAt: string;
  servicedBy: string;
  updatedAt: string;
  withoutViCareUser: boolean;
}

export interface CustomerNumberWithAccounts {
  customerNumber: string;
  accounts?: AccessListUserData[];
  companyName?: string;
}

export interface AccessListUserByCustomerNumberData {
  name: string;
  value: AccessListUserByCustomerNumberValue[];
}

export interface AccessListUserByCustomerNumberValue {
  email: string;
  familyName: string;
  firstName: string;
  iamId: string;
}
export interface CreateInstallationPayload {
  address: TransferInstallationAddress;
  description: string;
  gateway: GatewayData;
  heatingType?: HeatingType;
  installationOwnerData?: InstallationOwnerData;
  withoutOwner: boolean;
}

export interface CreateCommercialInstallationPayload {
  accessLevel: InvitationAccessLevel;
  address: TransferInstallationAddress;
  description: string;
  email: string;
  gatewayPassword: string;
  gatewaySerial: string;
  withoutInvitation: boolean;
}

export enum DocumentType {
  SYSTEM_EXAMPLE = 'VAB',
  CONNECTION_AND_WIRING_DIAGRAM = 'VAP',
  USER_MANUAL = 'VBA',
  OPERATOR_DOCUMENT = 'VBD',
  PARTS_LIST = 'VET',
  EU_DECLARATION_OF_CONFORMITY = 'VKE',
  COMPATIBILITY_LIST = 'KOM',
  PROSPECT = 'MDS',
  ASSEMBLY_INSTRUCTIONS = 'VMA',
  SERVICE_INSTRUCTIONS = 'VSA',
  SAFETY_DATA_SHEETS = 'SID',
  SALES_CHECKLIST = 'VCL',
  MAINTENANCE_CHECKLIST = 'WCL',
  CERTIFICATES = 'CER',
  DATASHEET = 'VDP',
  PLANNING_INSTRUCTIONS = 'VPA',
  PRICE_SHEET = 'VPL',
  PRICE_LIST = 'VKP',
}

export enum HeatingType {
  None = 'None',
  FloorHeating = 'Floor Heating',
  Radiators = 'Radiators',
  Both = 'Both',
  Undefined = 'Undefined',
}

export interface EquipmentLiteDetail {
  equipmentNumber: string;
  serialMaterialNumber: number;
  materialShortDescription: string;
  guarantyStartDate: number;
  guarantyEndDate: number;
  orderingCustomer: string;
  goodsReceipient: string;
  materialGroup: number | string;
}

export const forbiddenEquipmentMaterialGroups = ['T580715', 652, 374];
Object.freeze(forbiddenEquipmentMaterialGroups);
